import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiLock, FiUnlock, FiEye, FiEyeOff, FiHeart } = FiIcons;

const AccessGate = ({ onAccessGranted }) => {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [showCode, setShowCode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Valid access codes
  const validCodes = [
    'a9phzx5mwm',  // Original code
    'writing7k2',  // Writing themed
    'momentum4x9', // Momentum themed
    'purpose8m1',  // Purpose themed
    'gospel3n7',   // Gospel themed
    'author2k8'    // Bonus author themed
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    // Simulate a brief loading delay for better UX
    await new Promise(resolve => setTimeout(resolve, 800));

    const trimmedCode = code.trim().toLowerCase();
    
    if (validCodes.includes(trimmedCode)) {
      // Store access in localStorage
      localStorage.setItem('gospelwise-access', 'granted');
      localStorage.setItem('gospelwise-access-code', trimmedCode);
      localStorage.setItem('gospelwise-access-timestamp', new Date().toISOString());
      
      onAccessGranted();
    } else {
      setError('Invalid access code. Please check your code and try again.');
    }
    
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-orange-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="w-full max-w-md"
      >
        {/* Access Gate Card */}
        <div className="bg-white/90 backdrop-blur-md rounded-3xl p-8 shadow-2xl border border-white/30">
          {/* Header */}
          <div className="text-center mb-8">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
              className="w-20 h-20 bg-gradient-to-br from-[#ff8057] to-[#2558ab] rounded-2xl mx-auto mb-4 flex items-center justify-center shadow-lg"
            >
              <SafeIcon icon={FiLock} className="text-3xl text-white" />
            </motion.div>
            
            <motion.h1
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-3xl font-bold bg-gradient-to-r from-[#2558ab] to-[#ff8057] bg-clip-text text-transparent mb-2"
            >
              GospelWise Author
            </motion.h1>
            
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-slate-600 text-lg"
            >
              Word Tracking Dashboard
            </motion.p>
          </div>

          {/* Access Form */}
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            onSubmit={handleSubmit}
            className="space-y-6"
          >
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-3">
                Enter Your Access Code
              </label>
              <div className="relative">
                <input
                  type={showCode ? 'text' : 'password'}
                  value={code}
                  onChange={(e) => {
                    setCode(e.target.value);
                    setError('');
                  }}
                  className="w-full p-4 pr-12 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-[#ff8057] focus:border-[#ff8057] transition-all duration-300 text-lg tracking-wider"
                  placeholder="Enter access code"
                  required
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowCode(!showCode)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                  disabled={isLoading}
                >
                  <SafeIcon icon={showCode ? FiEyeOff : FiEye} className="text-xl" />
                </button>
              </div>
            </div>

            {error && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg text-sm"
              >
                {error}
              </motion.div>
            )}

            <motion.button
              type="submit"
              disabled={isLoading || !code.trim()}
              whileHover={{ scale: isLoading ? 1 : 1.02 }}
              whileTap={{ scale: isLoading ? 1 : 0.98 }}
              className="w-full bg-gradient-to-r from-[#2558ab] to-[#ff8057] text-white p-4 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
            >
              {isLoading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Verifying...
                </>
              ) : (
                <>
                  <SafeIcon icon={FiUnlock} className="text-xl" />
                  Access Dashboard
                </>
              )}
            </motion.button>
          </motion.form>

          {/* Encouragement */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="mt-8 text-center"
          >
            <div className="flex items-center justify-center gap-2 mb-3">
              <SafeIcon icon={FiHeart} className="text-[#ff8057] text-lg" />
              <span className="text-sm font-medium text-slate-600">Inspiration</span>
              <SafeIcon icon={FiHeart} className="text-[#ff8057] text-lg" />
            </div>
            <p className="text-sm text-slate-500 italic leading-relaxed">
              "For we are God's handiwork, created in Christ Jesus to do good works, 
              which God prepared in advance for us to do." - Ephesians 2:10
            </p>
          </motion.div>
        </div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="text-center mt-6"
        >
          <p className="text-xs text-slate-400">
            <a style={{
      color: 'rgb(255, 128, 87)'
    }} 
              href="https://www.gospelwiseauthor.com/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-[#ff8057] hover:text-[#e5714d] transition-colors font-medium"
            >Your Christian writing journey starts here</a>• Made with ❤️ by CROSS + PEN</p>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default AccessGate;